<script lang="ts">
	import { CalendarDate } from "@internationalized/date";
	import currency from "currency.js";
	import * as Table from "#lib/web-ui/ui/table/index.js";
	import * as Tabs from "#lib/web-ui/ui/tabs/index.js";

	let {
		loanAmount,
		annualInterestRate,
		loanTermYears,
		startDate,
		taxes,
		insurance,
		hoa
	}: {
		loanAmount: currency;
		annualInterestRate: number;
		loanTermYears: number;
		startDate: CalendarDate;
		taxes: currency;
		insurance: currency;
		hoa: currency;
	} = $props();

	const monthNames = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec"
	];

	function createPaymentSchedule() {
		const additionalFees = taxes.add(insurance).add(hoa);
		const startingBalance = Number(loanAmount);
		const rate = annualInterestRate / 100 / 12;
		const nper = loanTermYears * 12;
		const monthlyPayment =
			(startingBalance * (rate * (1 + rate) ** nper)) / ((1 + rate) ** nper - 1);

		let monthlyPaymentSchedule = [];
		let annualPaymentSchedule = [];
		let loanBalance = startingBalance;
		let currentPeriod = 1;
		let annualPrincipal = 0;
		let annualInterest = 0;
		let currentDate = new Date(startDate.year, startDate.month, startDate.day); // Start from startDate

		for (let period = 1; period <= nper; period++) {
			const interestPayment = loanBalance * rate;
			const principalPayment = monthlyPayment - interestPayment;
			loanBalance = loanBalance - principalPayment;

			monthlyPaymentSchedule.push({
				period,
				date: new CalendarDate(
					currentDate.getFullYear(),
					currentDate.getMonth(),
					currentDate.getDate()
				), // Add date for monthly
				interest: currency(interestPayment).format(),
				principal: currency(principalPayment).format(),
				additionalFees: currency(additionalFees).format(),
				balance: currency(loanBalance).format()
			});

			annualPrincipal += principalPayment;
			annualInterest += interestPayment;
			if (period % 12 === 0) {
				annualPaymentSchedule.push({
					period: currentPeriod,
					date: new CalendarDate(
						currentDate.getFullYear(),
						currentDate.getMonth(),
						currentDate.getDate()
					), // Add date for annual
					principal: currency(annualPrincipal).format(),
					interest: currency(annualInterest).format(),
					additionalFees: currency(additionalFees).format(),
					balance: currency(loanBalance).format()
				});
				currentPeriod++;
				annualPrincipal = 0;
				annualInterest = 0;
			}

			currentDate.setMonth(currentDate.getMonth() + 1); // Increment the date by one month
		}

		return {
			monthlyPaymentSchedule,
			annualPaymentSchedule
		};
	}

	const monthlyPaymentSchedule = $derived(createPaymentSchedule().monthlyPaymentSchedule);
	const annualPaymentSchedule = $derived(createPaymentSchedule().annualPaymentSchedule);
</script>

<div class="mt-6 w-full border-t pt-6">
	<h3 class="mb-4 text-lg font-medium">Amortization Schedule</h3>
	<Tabs.Root value="monthly">
		<Tabs.List>
			<Tabs.Trigger value="monthly">Monthly</Tabs.Trigger>
			<Tabs.Trigger value="annual">Annual</Tabs.Trigger>
		</Tabs.List>
		<Tabs.Content value="monthly">
			<Table.Root>
				<Table.Header>
					<Table.Row>
						<Table.Head>Period</Table.Head>
						<Table.Head>Principal</Table.Head>
						<Table.Head>Interest</Table.Head>
						<Table.Head>Taxes, Insurance & HOA</Table.Head>
						<Table.Head>Balance</Table.Head>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{#each monthlyPaymentSchedule as payment}
						<Table.Row>
							<Table.Cell>
								{monthNames[payment.date.month]}
								{payment.date.year}
							</Table.Cell>
							<Table.Cell>{payment.principal}</Table.Cell>
							<Table.Cell>{payment.interest}</Table.Cell>
							<Table.Cell>{payment.additionalFees}</Table.Cell>
							<Table.Cell>{payment.balance}</Table.Cell>
						</Table.Row>
					{/each}
				</Table.Body>
			</Table.Root>
		</Tabs.Content>
		<Tabs.Content value="annual">
			<Table.Root>
				<Table.Header>
					<Table.Row>
						<Table.Head>Period</Table.Head>
						<Table.Head>Principal</Table.Head>
						<Table.Head>Interest</Table.Head>
						<Table.Head>Taxes, Insurance & HOA</Table.Head>
						<Table.Head>Balance</Table.Head>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{#each annualPaymentSchedule as payment}
						<Table.Row>
							<Table.Cell>
								{payment.date.year}
							</Table.Cell>
							<Table.Cell class="text-right">{payment.principal}</Table.Cell>
							<Table.Cell class="text-right">{payment.interest}</Table.Cell>
							<Table.Cell class="text-right">{payment.additionalFees}</Table.Cell>
							<Table.Cell class="text-right">{payment.balance}</Table.Cell>
						</Table.Row>
					{/each}
				</Table.Body>
			</Table.Root>
		</Tabs.Content>
	</Tabs.Root>
</div>
